import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, OnDestroy, OnInit, inject, input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, distinctUntilChanged, map, takeUntil } from 'rxjs';

import { ProductContextDirective } from 'ish-core/directives/product-context.directive';
import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { ViewType } from 'ish-core/models/viewtype/viewtype.types';
import { ProductItemComponent } from 'ish-shared/components/product/product-item/product-item.component';

/**
 * The Product List Component displays a list of products.
 *
 * @example
 * <ish-product-list
 *               [products]="products$ | async"
 *               [categoryId]="selectedCategoryId$ | async"
 *               [viewType]="viewType$ | async"
 * ></ish-product-list>
 */
@Component({
  selector: 'ish-product-list',
  templateUrl: './product-list.component.all.html',

  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ProductItemComponent, ProductContextDirective, TranslateModule],
})
export class ProductListComponent implements OnInit, OnChanges, OnDestroy {
  private shoppingFacade = inject(ShoppingFacade);
  private gtmFacade = inject(GoogleTagManagerFacade);
  private activatedRoute = inject(ActivatedRoute);

  readonly products = input.required<string[]>();
  readonly categoryId = input<string>(undefined);
  readonly viewType = input<ViewType>('grid');
  readonly isPaging = input<boolean>(false);

  listingLoading$: Observable<boolean>;
  isSorting = false;
  previousProductSKU: string[] = [];

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.listingLoading$ = this.shoppingFacade.productListingLoading$;

    this.activatedRoute.queryParamMap
      .pipe(
        map(params => params.get('sorting')),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(sorting => {
        if (sorting !== undefined) {
          this.isSorting = true;
          setTimeout(() => (this.isSorting = false), 1000);
        }
      });
  }
  ngOnChanges(): void {
    const eventItem = this.products().filter(sku => !this.previousProductSKU.includes(sku));
    this.previousProductSKU = this.products();
    const gtmProduct = eventItem.map((item: string) => ({ sku: item }));
    if (gtmProduct.length > 0) {
      this.gtmFacade.triggerGTMEvent('view_item_list', gtmProduct);
    }
  }

  get isList() {
    return this.viewType() === 'list';
  }

  get isGrid() {
    return !this.isList;
  }

  get colSize(): string {
    const gridSize: string = this.viewType()?.charAt(this.viewType().length - 1);
    switch (gridSize) {
      case '2':
        return '6';
      case '3':
        return '4';
      case '4':
        return '3 price-adjustment-4-column-grid';
      case '5':
        return '2 price-adjustment-5-column-grid';
      default:
        return '4';
    }
  }

  get skeletonCount(): number {
    // Use fixed skeleton counts based on view type
    return this.isList ? 5 : 12;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
